/**
 * WebGL Performance Monitoring Utilities
 */

import type { WebGLDemoMetadata, WebGLPerformanceMetrics } from '@/types/webgl';
import { logger } from '@/lib/utils/logger';

// Performance monitoring constants
export const PERFORMANCE_CONSTANTS = {
  DEFAULT_UPDATE_INTERVAL: 60, // frames
  MAX_WARNING_COUNT: 5,
  DEFAULT_FPS_SAMPLES: 60,
  NANOSECONDS_TO_MS: 1000000,
  BYTES_TO_MB: 1024 * 1024,
  DEFAULT_THRESHOLDS: {
    fpsWarningRatio: 0.8,
    memoryWarningMB: 512,
    frameTimeWarningMs: 16.67 // ~60fps
  }
} as const;

export interface PerformanceThresholds {
  fpsWarningRatio: number; // Ratio of target FPS to trigger warning (default: 0.8)
  memoryWarningMB: number; // Memory usage in MB to trigger warning
  frameTimeWarningMs: number; // Frame time in ms to trigger warning
}

export interface PerformanceMonitor {
  logInitialization(): void;
  startFrame(): void;
  endFrame(): void;
  getMetrics(): WebGLPerformanceMetrics;
  dispose(): void;
  updateThresholds(thresholds: Partial<PerformanceThresholds>): void;
}

/**
 * Create a performance monitor for a WebGL demo
 */
export function createPerformanceMonitor(
  metadata: WebGLDemoMetadata, 
  customThresholds?: Partial<PerformanceThresholds>
): PerformanceMonitor | null {
  if (process.env.NODE_ENV !== 'development' || !metadata.performance) {
    return null;
  }

  let frameCount = 0;
  let frameStartTime = 0;
  let totalFrameTime = 0;
  let isDisposed = false;
  let performanceWarningCount = 0;
  const MAX_WARNINGS = PERFORMANCE_CONSTANTS.MAX_WARNING_COUNT;
  
  let thresholds: PerformanceThresholds = {
    ...PERFORMANCE_CONSTANTS.DEFAULT_THRESHOLDS,
    ...customThresholds
  };

  const monitor: PerformanceMonitor = {
    logInitialization() {
      if (isDisposed) return;
      
      logger.info(`🎮 ${metadata.name} Performance Monitor Initialized`, {
        targetFps: metadata.performance?.targetFps,
        maxMemory: metadata.performance?.maxMemoryMB,
        gpuIntensive: metadata.performance?.gpuIntensive
      });
    },

    startFrame() {
      if (isDisposed) return;
      frameStartTime = performance.now();
    },

    endFrame() {
      if (isDisposed) return;
      
      const frameEndTime = performance.now();
      const frameTime = frameEndTime - frameStartTime;
      totalFrameTime += frameTime;
      frameCount++;

      // Log performance every N frames (roughly once per second at 60fps)
      if (frameCount % PERFORMANCE_CONSTANTS.DEFAULT_UPDATE_INTERVAL === 0) {
        const avgFrameTime = totalFrameTime / frameCount;
        const fps = 1000 / avgFrameTime;
        
        if (metadata.performance && fps < metadata.performance.targetFps * thresholds.fpsWarningRatio && performanceWarningCount < MAX_WARNINGS) {
          logger.warn(`⚠️ ${metadata.name} Performance Warning`, {
            currentFps: Math.round(fps),
            targetFps: metadata.performance.targetFps,
            avgFrameTime: Math.round(avgFrameTime * 100) / 100,
            warningCount: ++performanceWarningCount
          });
          
          if (performanceWarningCount >= MAX_WARNINGS) {
            logger.info(`Performance warnings suppressed for ${metadata.name} (max ${MAX_WARNINGS} reached)`);
          }
        }
      }
    },

    getMetrics(): WebGLPerformanceMetrics {
      const currentTime = performance.now();
      const avgFrameTime = frameCount > 0 ? totalFrameTime / frameCount : 0;
      const fps = avgFrameTime > 0 ? 1000 / avgFrameTime : 0;
      const memoryInfo = getMemoryUsage();

      return {
        fps: Math.round(fps),
        frameTime: Math.round(avgFrameTime * 100) / 100,
        memoryUsage: memoryInfo.used * 1024 * 1024, // Convert back to bytes for consistency
        drawCalls: 0, // Would need WebGL context to track this
        triangles: 0, // Would need WebGL context to track this
        lastUpdate: currentTime,
        renderTime: avgFrameTime
      };
    },

    dispose() {
      if (isDisposed) return;
      
      isDisposed = true;
      logger.info(`🏁 ${metadata.name} Performance Monitor Disposed`, {
        totalFrames: frameCount,
        avgFps: frameCount > 0 ? Math.round(1000 / (totalFrameTime / frameCount)) : 0
      });
    },

    updateThresholds(newThresholds: Partial<PerformanceThresholds>) {
      if (isDisposed) return;
      thresholds = { ...thresholds, ...newThresholds };
      logger.debug(`Updated performance thresholds for ${metadata.name}`, thresholds);
    }
  };

  return monitor;
}

/**
 * WebGL Context Performance Utilities
 */
interface TimerQueryExtension {
  TIME_ELAPSED_EXT: number;
  QUERY_RESULT_AVAILABLE_EXT: number;
  QUERY_RESULT_EXT: number;
  createQueryEXT(): WebGLQuery | null;
  deleteQueryEXT(query: WebGLQuery): void;
  beginQueryEXT(target: number, query: WebGLQuery): void;
  endQueryEXT(target: number): void;
  getQueryObjectEXT(query: WebGLQuery, pname: number): unknown;
}

export class WebGLPerformanceTracker {
  private gl: WebGLRenderingContext | WebGL2RenderingContext;
  private ext: TimerQueryExtension | null = null;
  private queries: (WebGLQuery | null)[] = [];
  private queryIndex = 0;
  private isWebGL2: boolean;
  private isDisposed = false;

  constructor(gl: WebGLRenderingContext | WebGL2RenderingContext) {
    this.gl = gl;
    this.isWebGL2 = gl instanceof WebGL2RenderingContext;
    
    try {
      this.ext = gl.getExtension('EXT_disjoint_timer_query') || 
                 gl.getExtension('EXT_disjoint_timer_query_webgl2');
      
      if (!this.ext) {
        logger.warn('GPU timing extension not available - performance tracking will be limited');
      }
    } catch (error) {
      logger.error('Failed to initialize GPU timing extension', error);
      this.ext = null;
    }
  }

  startGPUTiming(): void {
    if (this.isDisposed || !this.ext) return;

    try {
      const query = this.isWebGL2 
        ? (this.gl as WebGL2RenderingContext).createQuery() 
        : this.ext.createQueryEXT();
      
      if (!query) return;

      if (this.isWebGL2) {
        (this.gl as WebGL2RenderingContext).beginQuery(this.ext.TIME_ELAPSED_EXT, query);
      } else {
        this.ext.beginQueryEXT(this.ext.TIME_ELAPSED_EXT, query);
      }

      this.queries[this.queryIndex] = query;
    } catch (error) {
      logger.error('Failed to start GPU timing', error);
    }
  }

  endGPUTiming(): number | null {
    if (this.isDisposed || !this.ext) return null;

    const query = this.queries[this.queryIndex];
    if (!query) return null;

    try {
      // End the current query
      if (this.isWebGL2) {
        (this.gl as WebGL2RenderingContext).endQuery(this.ext.TIME_ELAPSED_EXT);
      } else {
        this.ext.endQueryEXT(this.ext.TIME_ELAPSED_EXT);
      }

      // Check previous query result (double buffering)
      const prevQueryIndex = (this.queryIndex + 1) % 2;
      const prevQuery = this.queries[prevQueryIndex];
      
      this.queryIndex = prevQueryIndex;

      if (!prevQuery) return null;

      // Check if previous result is available
      const available = this.isWebGL2
        ? (this.gl as WebGL2RenderingContext).getQueryParameter(prevQuery, (this.gl as WebGL2RenderingContext).QUERY_RESULT_AVAILABLE)
        : this.ext.getQueryObjectEXT(prevQuery, this.ext.QUERY_RESULT_AVAILABLE_EXT);

      if (available) {
        const timeElapsed = this.isWebGL2
          ? (this.gl as WebGL2RenderingContext).getQueryParameter(prevQuery, (this.gl as WebGL2RenderingContext).QUERY_RESULT)
          : this.ext.getQueryObjectEXT(prevQuery, this.ext.QUERY_RESULT_EXT);

        return (timeElapsed as number) / PERFORMANCE_CONSTANTS.NANOSECONDS_TO_MS;
      }

      return null;
    } catch (error) {
      logger.error('Failed to end GPU timing', error);
      return null;
    }
  }

  dispose(): void {
    if (this.isDisposed) return;
    
    this.isDisposed = true;
    
    this.queries.forEach(query => {
      if (query) {
        try {
          if (this.gl instanceof WebGL2RenderingContext) {
            this.gl.deleteQuery(query);
          } else if (this.ext?.deleteQueryEXT) {
            this.ext.deleteQueryEXT(query);
          }
        } catch (error) {
          logger.error('Failed to delete GPU query', error);
        }
      }
    });
    this.queries = [];
    this.ext = null;
  }
}

interface MemoryInfo {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

interface PerformanceWithMemory extends Performance {
  memory?: MemoryInfo;
}

/**
 * Memory usage tracking utilities
 */
export function getMemoryUsage(): { used: number; total: number; percentage: number } {
  const perfWithMemory = performance as PerformanceWithMemory;
  const memory = perfWithMemory.memory;
  
  if (!memory) {
    return { used: 0, total: 0, percentage: 0 };
  }

  const used = memory.usedJSHeapSize;
  const total = memory.totalJSHeapSize;
  const percentage = total > 0 ? (used / total) * 100 : 0;

  return {
    used: Math.round(used / PERFORMANCE_CONSTANTS.BYTES_TO_MB),
    total: Math.round(total / PERFORMANCE_CONSTANTS.BYTES_TO_MB),
    percentage: Math.round(percentage)
  };
}

/**
 * FPS counter utility
 */
export class FPSCounter {
  private frames: number[] = [];
  private lastTime = performance.now();
  private readonly maxSamples: number;

  constructor(maxSamples = PERFORMANCE_CONSTANTS.DEFAULT_FPS_SAMPLES) {
    this.maxSamples = maxSamples;
  }

  update(): number {
    const currentTime = performance.now();
    const deltaTime = currentTime - this.lastTime;
    
    // Skip invalid frames (too fast or too slow)
    if (deltaTime > 0 && deltaTime < 1000) {
      this.frames.push(1000 / deltaTime);
      
      // Keep only the specified number of samples
      if (this.frames.length > this.maxSamples) {
        this.frames.shift();
      }
    }
    
    this.lastTime = currentTime;

    // Return average FPS, handle empty array
    return this.frames.length > 0 
      ? this.frames.reduce((sum, fps) => sum + fps, 0) / this.frames.length 
      : 0;
  }

  getCurrentFPS(): number {
    return this.frames.length > 0 ? this.frames[this.frames.length - 1] : 0;
  }

  getAverageFPS(): number {
    return this.frames.length > 0 
      ? this.frames.reduce((sum, fps) => sum + fps, 0) / this.frames.length 
      : 0;
  }

  reset(): void {
    this.frames = [];
    this.lastTime = performance.now();
  }
}